// Пульт подотчёта. Стартовый экран финансового директора: вход открывает его, и без единого
// клика видно, у кого сколько висит и сколько времени.
//
// Одна строка — один человек с деньгами на руках (правило 2). Над списком сказано, сколько
// их в ЭТОЙ выборке и на какой момент срез.
//
// Порядок по умолчанию — ПО ДАВНОСТИ: сверху тот, у кого висит дольше всех. Так требует
// «Утро финансового директора: ноль кликов» (`ПРАВИЛА.md`), и так же отвечает помощник на
// вопрос «кто дольше всех не отчитывается». Второй порядок, по сумме, отвечает на другой
// вопрос — «где больше всего», — и переключается одним нажатием.
//
// Сортировка серверная: сортировать в браузере значит сначала вытащить всех, а список будет
// прыгать под курсором при каждом обновлении.
//
// Правило 11: при смене отбора строки НЕ исчезают — прошлые данные держатся до приезда
// свежих, скелет рисуется только когда данных нет даже в кэше прошлой сессии.

import { useRef } from 'react';

import { RiFilterOffLine, RiPieChartLine, RiWallet3Line } from '@remixicon/react';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import { ListPane } from '@/layout/ListPane';
import { exportReconciliation } from '@/shared/api/endpoints/reports';
import { ExportButton } from '@/shared/reports/ExportButton';
import type { PersonOut } from '@/shared/api/endpoints/accountable';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { parseMoment } from '@/shared/time/format';
import { ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { SearchFilter } from '@/shared/ui/search-filter';
import * as Button from '@/shared/ui/button';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Подсказка } from '@/shared/hints/Подсказка';
import * as SegmentedControl from '@/shared/ui/segmented-control';
import * as Switch from '@/shared/ui/switch';
import type { ListResponse } from '@/shared/api/types';

import { PersonRow } from '../components/PersonRow';
import { PERSON_FORMS } from '../dictionaries';
import { usePersons } from '../queries';
import {
  FILTER_ENTITY,
  FILTER_ORDER,
  FILTER_OVER_LIMIT,
  ORDER_BY_AGE,
  ORDER_BY_AMOUNT,
  personPath,
  viewPath,
  withPanel,
  PANEL_SUMMARY,
  VIEW_DESK,
} from '../route';

export interface AccountableDeskProps {
  search: SearchState;
  selectedId: string | null;
  /** Поменять отбор в адресе: источник правды — адрес, а не состояние компонента. */
  onFilter: (key: string, value: string | null) => void;
  onQuery: (value: string) => void;
}

export function AccountableDesk({ search, selectedId, onFilter, onQuery }: AccountableDeskProps) {
  // Подсказка указывает на переключатель ссылкой: обёртка вокруг него сместила бы ритм шапки.
  const якорьПорядка = useRef<HTMLDivElement>(null);
  const юрлицо = search.filters[FILTER_ENTITY];
  const толькоВышеПорога = search.filters[FILTER_OVER_LIMIT] === 'true';

  const поСумме = search.filters[FILTER_ORDER] === ORDER_BY_AMOUNT;

  const запрос = usePersons({
    legal_entity_id: юрлицо,
    q: search.q,
    order: поСумме ? ORDER_BY_AMOUNT : undefined,
  });
  const юрлица = useLegalEntitiesQuery();

  const данные = запрос.data;
  const строки = отобрать(данные?.items ?? [], толькоВышеПорога);
  const срез = данные?.generated_at !== undefined ? parseMoment(данные.generated_at) : null;

  return (
    <ListPane
      // Пока данных нет — НЕ «0 подотчётных лиц»: ноль означает «никого нет», и это враньё
      // на две секунды загрузки. Поймано живым прогоном на канале в 229 мс.
      caption={
        данные === undefined
          ? 'Подотчёт'
          : countLabel(толькоВышеПорога ? строки.length : данные.total, PERSON_FORMS)
      }
      sliceAt={срез}
      width='числа'
      appliedFilters={[
        юрлицо === undefined ? null : 'юрлицо',
        толькоВышеПорога ? 'только выше порога' : null,
      ].filter((имя): имя is string => имя !== null)}
      search={
        <SearchFilter
          label='Поиск подотчётного лица по фамилии'
          placeholder='Фамилия'
          value={search.q ?? ''}
          onApply={onQuery}
        />
      }
      note={
        <>
          {/* Порядок стоит НЕ среди отборов, а на виду рядом с поиском, и это не косметика:
              он меняет не состав списка, а сам вопрос, на который список отвечает. «Кто дольше»
              и «у кого больше» — два разных утра, и выбор между ними человек делает раньше,
              чем сужает выборку юрлицом. Спрятать его под «Отборы» значит спрятать главную
              развилку пульта за нажатием. */}
          <div ref={якорьПорядка}>
            <SegmentedControl.Root
              value={поСумме ? ORDER_BY_AMOUNT : ORDER_BY_AGE}
              onValueChange={(next) =>
                onFilter(FILTER_ORDER, next === ORDER_BY_AMOUNT ? ORDER_BY_AMOUNT : null)
              }
            >
              <SegmentedControl.List>
                <SegmentedControl.Trigger value={ORDER_BY_AGE}>Кто дольше</SegmentedControl.Trigger>
                <SegmentedControl.Trigger value={ORDER_BY_AMOUNT}>
                  У кого больше
                </SegmentedControl.Trigger>
              </SegmentedControl.List>
            </SegmentedControl.Root>
          </div>
          {/* Условие, а не просто показ. Полевой руководитель видит на пульте ровно одну
              строку — себя, и «сверху тот, у кого висит дольше» на списке из одного человека
              не объясняет ничего, зато тратит единственный показ, который есть у урока за всё
              время. Урок при этом не сгорает: он покажется тому, у кого список длиннее. */}
          <Подсказка
            урок='порядок-подотчёта'
            якорь={якорьПорядка}
            когда={(запрос.data?.items.length ?? 0) > 1}
          />
          {/* Сводка за период — ТОЛЬКО на узком экране.
              На компьютере те же четыре числа стоят правой колонкой и открываются без единого
              нажатия: «утро финдиректора, ноль кликов». В книжной ориентации планшета правой
              колонки нет вовсе, поверхность одна и её занимает список людей с деньгами — и без
              этой кнопки числа стали бы недостижимы, то есть планшет потерял бы против
              компьютера целый экран. Одно нажатие вместо нуля здесь честная цена. */}
          <Link
            to={viewPath(VIEW_DESK, withPanel(search, PANEL_SUMMARY, юрлицо ?? 'все'))}
            className='hidden items-center gap-2 rounded-10 border border-stroke-soft-200 px-3 py-2 text-label-sm text-text-strong-950 узко:flex'
          >
            <RiPieChartLine className='size-4 shrink-0 text-text-sub-600' />
            Сводка за период
          </Link>
        </>
      }
      filters={
        <>
          <SelectField
            id='money-legal-entity'
            label='Юридическое лицо'
            placeholder='все юридические лица'
            value={юрлицо ?? NO_VALUE}
            noneLabel='все'
            options={(юрлица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.short_name ?? строка.name,
            }))}
            pending={юрлица.isPending}
            failed={юрлица.isError}
            failedError={юрлица.error as ApiError | null}
            emptyNote='Юридических лиц в справочнике нет. Их заводит администратор в настройках — до этого отбирать не по чему.'
            onChange={(value) => onFilter(FILTER_ENTITY, value === NO_VALUE ? null : value)}
          />
          {/* «Своего» — не украшение. Порог у каждого человека свой (он в его карточке), и
              «выше порога» оставляло вопрос «какого». Назвать число здесь нельзя: числа нет,
              их столько же, сколько людей. Соседний отбор в очереди приёмки порог называет
              («от 5 000,00 ₽») ровно потому, что там он один на всех. */}
          <label className='flex items-center justify-between gap-3 text-paragraph-sm text-text-sub-600'>
            Только тех, кто выше своего порога
            <Switch.Root
              checked={толькоВышеПорога}
              onCheckedChange={(checked) => onFilter(FILTER_OVER_LIMIT, checked ? 'true' : null)}
            />
          </label>
          {/* Акт печатают с тем же отбором, который сейчас на экране: кнопка стоит рядом с
              отбором, а не в другом углу, иначе однажды подпишут не тот список. Но она
              ДЕЙСТВИЕ, а не отбор, поэтому отделена чертой: без черты она читалась четвёртым
              полем отбора и терялась среди них. */}
          <div className='border-t border-stroke-soft-200 pt-3'>
            <ExportButton
              собрать={() =>
                exportReconciliation(юрлицо === undefined ? {} : { legal_entity_id: юрлицо })
              }
            />
          </div>
        </>
      }
    >
      <ScreenState<ListResponse<PersonOut>>
        data={данные}
        error={запрос.error as never}
        subject='список людей с деньгами на руках'
        isEmpty={() => строки.length === 0}
        loading={<ListSkeleton />}
        empty={
          <Пусто
            фильтруем={толькоВышеПорога || Boolean(search.q) || Boolean(юрлицо)}
            снятьОтбор={() => {
              onFilter(FILTER_OVER_LIMIT, null);
              onFilter(FILTER_ENTITY, null);
              onQuery('');
            }}
          />
        }
        onRetry={() => void запрос.refetch()}
      >
        {() => (
          // Список подписан: на этом экране есть второй перечень — «чего ещё не хватает» в
          // правой колонке, — и без подписи их не различить ни читалке с экрана, ни сквозной
          // проверке. Она искала строки подотчётных лиц, находила два пункта той сводки и
          // падала на пустом списке сумм.
          <ul aria-label='Подотчётные лица'>
            {строки.map((человек) => (
              <li key={человек.id}>
                <PersonRow
                  person={человек}
                  href={personPath(человек.id, search)}
                  selected={человек.id === selectedId}
                />
              </li>
            ))}
          </ul>
        )}
      </ScreenState>
    </ListPane>
  );
}

/**
 * Отбор «только выше порога» делается здесь, а не на сервере, и это осознанно: порог —
 * свойство человека, а не выборки, и ответ сервера от него не меняется. Всё остальное —
 * юрлицо и поиск — уезжает на сервер, потому что меняет ВЫБОРКУ и счётчик над списком.
 */
function отобрать(строки: readonly PersonOut[], толькоВышеПорога: boolean): PersonOut[] {
  return толькоВышеПорога ? строки.filter((строка) => строка.over_soft_limit) : [...строки];
}

// Пустое из-за отбора обязано давать не только причину, но и ДЕЙСТВИЕ. Отборов на пульте три,
// и человек, включивший два из них, ищет глазами, какой именно виноват: текст «снимите отбор»
// без кнопки — это задача, переложенная обратно на него. У «Людей» и «Объектов» кнопка есть,
// то есть грамматика разъезжалась внутри одного продукта.
function Пусто({ фильтруем, снятьОтбор }: { фильтруем: boolean; снятьОтбор: () => void }) {
  return фильтруем ? (
    <EmptyState
      icon={RiWallet3Line}
      title='Под этот отбор никого нет'
      description='Список пуст из-за отбора, а не потому, что денег ни на ком не висит.'
      action={
        <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={снятьОтбор}>
          <Button.Icon as={RiFilterOffLine} />
          Снять отбор
        </Button.Root>
      }
    />
  ) : (
    <EmptyState
      icon={RiWallet3Line}
      title='Денег под отчётом ни на ком нет'
      illustration='деньги'
      description='Все отчитались либо выдач ещё не было. Строка появится, как только финотдел оформит первую выдачу.'
    />
  );
}
